import {FastenService} from '../services/fasten.service';
import {Router} from '@angular/router';
import {inject} from '@angular/core';
import {
  CallbackPayload,
  ConfigService,
  ConnectErrorData,
  ConnectMode,
  ConnectPayload,
  MessageBusService,
  ProcessTefcaDirectAuthorizationResults
} from '@shared-library';
import {v4 as uuidv4} from 'uuid';
import {Observable, Subscription} from "rxjs";
import {SentryContextService} from "../services/sentry-context.service";


//ConnectHelper will redirect to the dashboard or a support form on complete. No return values
export function ConnectHelper(connectData: ConnectPayload): Subscription {
  const vaultApi = inject(FastenService) as FastenService;
  const configService = inject(ConfigService) as ConfigService;
  const router = inject(Router) as Router;
  const messageBusService = inject(MessageBusService) as MessageBusService;
  const sentryContextService = inject(SentryContextService) as SentryContextService;

  if (!connectData.external_state) {
    connectData.external_state = uuidv4();
  }

  //set a default connectMode if not present
  if(!connectData.connect_mode){
    connectData.connect_mode = ConnectMode.Popup
  }

  //publish the pending connection event
  messageBusService.publishOrgConnectionPending(connectData)


  let onSuccessNavigateByUrl = 'dashboard'
  if(connectData.org_connection_id){
    //if the org_connection_id is present, this means that the user is reconnecting an existing health system.
    // on success, we should sent the patient to the complete page, instead of the dashboard.
    onSuccessNavigateByUrl = 'dashboard/complete'
  }
  let connectObservable: Observable<CallbackPayload>
  if(connectData.connect_mode == ConnectMode.Websocket){
    connectObservable = vaultApi.accountConnectWithWebsocket(connectData)
    //todo: disconnect on success or error
    // https://rxjs.dev/api/webSocket/webSocket .complete()
  } else {
    connectObservable = vaultApi.accountConnectWithPopup(connectData)
  }
  return connectObservable.subscribe(
    (orgConnectionCallbackData) => {
      if(!orgConnectionCallbackData){
        return //wait for redirect
      }

      //Note: this code will only run when this.connectMode == popup
      //when connectMode == 'redirect', the user is redirected in the same window, and this code is never executed.

      messageBusService.publishOrgConnectionComplete(orgConnectionCallbackData)

      configService.vaultProfileAddConnectedAccount(orgConnectionCallbackData)

      if (configService.systemConfig$.tefcaMode && !orgConnectionCallbackData.error) {
        orgConnectionCallbackData.linked_connections?.forEach((linkedConnection) => {
          if (!linkedConnection.vault_profile_connection_id) {
            return;
          }

          configService.vaultProfileAddConnectedAccount(linkedConnection);
        });
      }

      // redirect the browser back to the search page (see below for support request page if an error occurred)
      router.navigateByUrl(onSuccessNavigateByUrl);
    },
    (err) => {
      try {
        let errData: ConnectErrorData;
        //check if type of error is a string or an object.
        //if it's a string, parse it as JSON
        if (typeof err === 'string') {
          errData = JSON.parse(err);
        } else if (typeof err.message === 'string') {
          errData = JSON.parse(err.message);
        } else {
          errData = err
        }

        sentryContextService.setConnectionContext(connectData.org_connection_id)
        console.error("popup error data", err)

        //var errData = JSON.parse(err.toString())
        if (errData.error == 'timeout') {
          return router.navigateByUrl(onSuccessNavigateByUrl)
        } else {
          console.error('an error occurred while attempting to connect health system', err)
          messageBusService.publishOrgConnectionComplete({
            org_connection_id: connectData.org_connection_id,
            endpoint_id: connectData.endpoint_id,
            brand_id: connectData.brand_id,
            portal_id: connectData.portal_id,

            request_id: errData.request_id,
            error: errData.error || 'unknown_error_during_connect',
            error_description: errData.error_description || 'an unknown error occurred during the connection process',
          })
          router.navigate(['form/support'], {
            queryParams: {
              "error": errData.error || 'unknown_error_during_connect',
              "error_description": errData.error_description || 'an unknown error occurred during the connection process',

              "brand_id": connectData.brand_id,
              "portal_id": connectData.portal_id,
              "endpoint_id": connectData.endpoint_id,
              "org_connection_id": connectData.org_connection_id,
              "vault_profile_connection_id": connectData.vault_profile_connection_id,

              "external_id": connectData.external_id,
              "external_state": connectData.external_state || errData.external_state,
              "request_id": errData.request_id,
            }
          })
        }
      } catch (e){
        console.error("caught error parsing error data", e)
        messageBusService.publishOrgConnectionComplete({
          org_connection_id: connectData.org_connection_id,
          endpoint_id: connectData.endpoint_id,
          brand_id: connectData.brand_id,
          portal_id: connectData.portal_id,
          error: 'unknown_error_during_connect',
          error_description: 'an unknown error occurred during the connection process',
        })
        router.navigate(['form/support'], {
          queryParams: {
            "error": 'unknown_error_during_connect',
            "error_description": `an unknown error occurred during the connection process: ${err}`,

            "brand_id": connectData.brand_id,
            "portal_id": connectData.portal_id,
            "endpoint_id": connectData.endpoint_id,
            "org_connection_id": connectData.org_connection_id,
            "vault_profile_connection_id": connectData.vault_profile_connection_id,

            "external_id": connectData.external_id,
            "external_state": connectData.external_state || err['external_state'],
          }
        })
      }

      return null
    })
}
export {ProcessTefcaDirectAuthorizationResults};
